// https://stackoverflow.com/questions/59590915/how-to-use-auth0-with-react-admin

import { AuthPermission } from '@40seas/entities/auth';
import { SwitchRoles, UserRole } from '@40seas/entities/users';
import { useAppStore } from '@40seas/frontend/dashboard/contexts';
import { Resources } from '@40seas/frontend/dashboard/providers';
import { useUser } from '@40seas/frontend/libs/hooks';
import { useEffect, useState } from 'react';
import DefaultDataProvider from '../providers/defaultDataProvider';
import { isGuestRole } from '@40seas/frontend/libs/hooks';
import { useAuth } from './useAuth';

const useInitAuthProvider = (dataProvider: DefaultDataProvider) => {
    const { isAuthenticated, logout, isLoading: authLoading, user } = useAuth();

    const { role } = useUser();

    const { urlRole } = useAppStore();

    const { permissions, isLoading: permissionsLoading } = useGetPermissions(dataProvider);

    return {
        isLoading: permissionsLoading,
        permissions,
        login: () => {
            // This function is called from react admin login page
            // We are using auth0 login page so not sure if this is needed at all
            return Promise.reject('Login method should ne called');
        },
        logout: () => {
            if (isAuthenticated) {
                // Need to check for this as react-admin calls logout in case checkAuth failed
                logout({
                    returnTo: window.location.origin,
                    // federated: true // Need to check whether we need this property, have to be enabled to invalidate refresh token
                });
            }
            return Promise.resolve();
        },
        checkError: ({ status }: { status: number }) => {
            if (status === 401 || status === 403) {
                return Promise.reject();
            }
            return Promise.resolve();
        },
        checkAuth: () => {
            return isAuthenticated || authLoading || isGuestRole(role as UserRole) ? Promise.resolve() : Promise.reject();
        },
        getPermissions: async (): Promise<string[]> => {
            if (urlRole && permissions.includes('admin:full')) {
                const { data: permissions } = await dataProvider.customGet(Resources.Users, `roles/${urlRole}/permissions`);

                return mapPermissionsToStrings(permissions).concat(`${SwitchRoles}:full`);
            }

            return Promise.resolve(permissions);
        },
        getIdentity: () => {
            return Promise.resolve({
                id: '',
                fullName: user?.name,
                avatar: user?.picture,
            });
        },
    };
};

export default useInitAuthProvider;

const useGetPermissions = (dataProvider: DefaultDataProvider) => {
    const { tenant } = useAppStore();

    const [permissions, setPermissions] = useState<string[]>([]);
    const [isLoading, setLoading] = useState(true);

    useEffect(() => {
        const fetchPermissions = async () => {
            setLoading(true);
            try {
                const { data: permissions } = await dataProvider.customGet(Resources.Users, 'auth-permissions');
                setPermissions(mapPermissionsToStrings(permissions));
            } catch {
                setPermissions([]);
            } finally {
                setLoading(false);
            }
        };

        void fetchPermissions();
    }, [tenant]);

    return { permissions, isLoading };
};

function mapPermissionsToStrings(permissions: AuthPermission[]) {
    return permissions.map(({ resource, action, access }) => (access ? `${resource}:${action}:${access}` : `${resource}:${action}`));
}
